/**
 * Drawing on a still of the other party's picture: what a drawing holds and how it is painted
 *
 * A drawing is the still and the marks on it - pen strokes and texts - kept as shapes, not pixels, so the last one can be
 * taken back. Everything is in the drawing's own pixels: the canvas it is painted on is drawingSize() of the still.
 */

/** The ten colours - strong and far apart, so each stands out on any picture */
export const DRAWING_COLORS = [ '#ff1744', '#ff9100', '#ffea00', '#00e676', '#00e5ff', '#2962ff', '#d500f9', '#ff4081', '#ffffff', '#000000' ] as const

/** The pen width and the text height of each thickness, thinnest first, on a drawing whose longer side is REFERENCE_SIZE */
const PEN_WIDTHS = [ 3, 6, 10, 16, 24 ]
const TEXT_SIZES = [ 24, 34, 46, 62, 84 ]

/** How many thicknesses there are */
export const THICKNESSES = PEN_WIDTHS.length

/** The longer side the sizes are for - and what a smaller still is scaled up to, so lines and text stay sharp on the other side */
const REFERENCE_SIZE = 1280

/** The longest side a drawing has - more would only take bitrate from the video */
const MAX_SIZE = 1920

/** The size of a still that reports none - HD video */
const FALLBACK = { width: 1280, height: 720 }

export interface Size {
	width: number
	height: number
}

export interface Point {
	x: number
	y: number
}

export interface StrokeShape {
	kind: 'stroke'
	color: string
	width: number
	points: Point[]
}

export interface TextShape {
	kind: 'text'
	color: string
	size: number
	/** The text's top left corner */
	at: Point
	text: string
}

export type Shape = StrokeShape | TextShape

/** The marks on a drawing, and the marks as they were before each change - to take the change back */
export interface Marks {
	shapes: Shape[]
	history: Shape[][]
}

/** The font of a text mark - the system's own, which every window has */
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'

/**
 * The size of the drawing of a still: its longer side scaled to at least REFERENCE_SIZE and at most MAX_SIZE
 * @param width The still's width
 * @param height The still's height
 */
export function drawingSize(width: number, height: number): Size {
	if (width <= 0 || height <= 0) { return { ...FALLBACK } }

	const longer = Math.max(width, height)
	const scale = Math.min(Math.max(longer, REFERENCE_SIZE), MAX_SIZE) / longer

	return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

/**
 * How much a size is to be scaled for a drawing
 * @param size The drawing's size
 */
function scaleOf(size: Size) {
	return Math.max(size.width, size.height) / REFERENCE_SIZE
}

/**
 * A thickness kept to the ones there are
 * @param thickness 0 (thinnest) to THICKNESSES - 1
 */
function within(thickness: number) {
	return Math.min(Math.max(Math.round(thickness), 0), THICKNESSES - 1)
}

/**
 * The pen's width in the drawing's pixels
 * @param thickness 0 (thinnest) to THICKNESSES - 1
 * @param size The drawing's size
 */
export function penWidth(thickness: number, size: Size): number {
	return (PEN_WIDTHS[within(thickness)] ?? 1) * scaleOf(size)
}

/**
 * The height of a text in the drawing's pixels
 * @param thickness 0 (thinnest) to THICKNESSES - 1
 * @param size The drawing's size
 */
export function textSize(thickness: number, size: Size): number {
	return (TEXT_SIZES[within(thickness)] ?? 24) * scaleOf(size)
}

/** A drawing without marks */
export function emptyMarks(): Marks {
	return { shapes: [], history: [] }
}

/**
 * Add a mark
 * @param marks The marks so far
 * @param shape The new mark
 */
export function addShape(marks: Marks, shape: Shape): Marks {
	return { shapes: [ ...marks.shapes, shape ], history: [ ...marks.history, marks.shapes ] }
}

/**
 * Take every mark away - a change that can be taken back too
 * @param marks The marks so far
 */
export function clearShapes(marks: Marks): Marks {
	if (marks.shapes.length === 0) { return marks }
	return { shapes: [], history: [ ...marks.history, marks.shapes ] }
}

/**
 * Take back the last change: a mark added, or everything cleared
 * @param marks The marks so far
 */
export function undo(marks: Marks): Marks {
	const previous = marks.history[marks.history.length - 1]

	if (!previous) { return marks }
	return { shapes: previous, history: marks.history.slice(0, -1) }
}

/**
 * Whether there is a change to take back
 * @param marks The marks so far
 */
export function canUndo(marks: Marks): boolean {
	return marks.history.length > 0
}

/**
 * Where a pointer is on the drawing, from where it is on the canvas on screen - kept to the drawing's edges
 * @param clientX The pointer's position in the window
 * @param clientY The pointer's position in the window
 * @param box The canvas's place on screen, from getBoundingClientRect
 * @param size The drawing's size
 */
export function toDrawing(clientX: number, clientY: number, box: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>, size: Size): Point {
	const x = box.width > 0 ? ((clientX - box.left) / box.width) * size.width : 0
	const y = box.height > 0 ? ((clientY - box.top) / box.height) * size.height : 0

	return { x: Math.min(Math.max(x, 0), size.width), y: Math.min(Math.max(y, 0), size.height) }
}

/**
 * The colour a text is outlined in, so it reads on any picture: black - and white around black
 * @param color The text's colour
 */
export function outlineFor(color: string): string {
	return color === '#000000' ? '#ffffff' : '#000000'
}

/**
 * The CSS font of a text mark
 * @param size The text's height in pixels
 */
export function textFont(size: number): string {
	return `bold ${size}px ${FONT}`
}

/**
 * Paint one mark
 * @param ctx The canvas's 2D context
 * @param shape The mark
 */
function paintShape(ctx: CanvasRenderingContext2D, shape: Shape) {
	ctx.save()
	if (shape.kind === 'stroke') {
		const [ first, ...rest ] = shape.points

		if (first && rest.length === 0) {
			// A tap: a dot as wide as the pen
			ctx.fillStyle = shape.color
			ctx.beginPath()
			ctx.arc(first.x, first.y, shape.width / 2, 0, Math.PI * 2)
			ctx.fill()
		} else if (first) {
			ctx.strokeStyle = shape.color
			ctx.lineWidth = shape.width
			ctx.lineCap = 'round'
			ctx.lineJoin = 'round'
			ctx.beginPath()
			ctx.moveTo(first.x, first.y)

			for (const point of rest) { ctx.lineTo(point.x, point.y) }
			ctx.stroke()
		}
	} else {
		ctx.font = textFont(shape.size)
		ctx.textBaseline = 'top'
		ctx.lineJoin = 'round'
		ctx.lineWidth = Math.max(2, shape.size / 6)
		ctx.strokeStyle = outlineFor(shape.color)
		ctx.strokeText(shape.text, shape.at.x, shape.at.y)
		ctx.fillStyle = shape.color
		ctx.fillText(shape.text, shape.at.x, shape.at.y)
	}
	ctx.restore()
}

/**
 * Paint a drawing: the still filling the canvas, then the marks in the order they were made
 * @param ctx The canvas's 2D context
 * @param still The still of the other party's picture
 * @param size The drawing's size - the canvas's
 * @param shapes The marks
 */
export function paintDrawing(ctx: CanvasRenderingContext2D, still: CanvasImageSource, size: Size, shapes: readonly Shape[]) {
	ctx.drawImage(still, 0, 0, size.width, size.height)

	for (const shape of shapes) { paintShape(ctx, shape) }
}

/**
 * The file name of a saved picture: what it is and when it was taken - letters, digits and dashes only
 * @param prefix What it is, e.g. "skaermbillede"
 * @param when When it was taken
 */
export function snapshotFilename(prefix: string, when: Date): string {
	const two = (value: number) => String(value).padStart(2, '0')
	const date = `${when.getFullYear()}-${two(when.getMonth() + 1)}-${two(when.getDate())}`
	const time = `${two(when.getHours())}${two(when.getMinutes())}${two(when.getSeconds())}`

	return `${prefix}-${date}-${time}.png`
}
